import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import { verifyOTP, resendOTP } from "@services/auth";
import { tryGetSinglePaymentPendingFacilityForPricing } from "@utils/verifiedFacilityRedirect";
import { useNavigate, useLocation } from "react-router-dom";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
import { setLandingVerticalSession } from "@services/verticals";
import {
  clearPendingSignupVerification,
  getPendingSignupVerification,
} from "@utils/signupVerification";

const SignUpContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
    alignItems: "flex-start",
    paddingTop: "20px",
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 0px",
  position: "relative",
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  gap: 16,
  [theme.breakpoints.down("lg")]: {
    gap: 12,
    flexDirection: "column",
    padding: "12px",
    maxWidth: "900px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "16px 8px",
    gap: 10,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 6px",
    gap: 8,
  },
}));

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 68.593px at 298.8px 124.35px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 6.0674px at 1471.2px 363.44px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 57.671px at 682.2px 879.48px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});

const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "620px",
  height: "520px",
  backgroundImage: `url(${ContentUsBgImage})`,
  borderRadius: "32px",
  border: "1px solid #ffffff",
  overflow: "hidden",
  marginRight: "-80px",
  flexShrink: 0,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  [theme.breakpoints.down("lg")]: {
    width: "480px",
    height: "400px",
    marginRight: "-20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    height: "auto",
    minHeight: "240px",
    aspectRatio: "16 / 10",
    marginRight: 0,
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minHeight: "240px",
    borderRadius: "16px",
  },
}));

const LeftPanelContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "95.5px",
  top: "101px",
  width: "470px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    left: "32px",
    top: "48px",
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    padding: "16px 12px 12px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "64px",
  lineHeight: "1.1",
  marginBottom: "40px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    fontSize: "42px",
    marginBottom: "24px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    marginBottom: "12px",
    lineHeight: "1.2",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    marginBottom: "12px",
    paddingTop: "48px",
    textAlign: "center",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "1.5",
  color: "#ffffff",
  width: "422px",
  textAlign: "center",
  whiteSpace: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
  [theme.breakpoints.down("lg")]: {
    width: "360px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    fontSize: "16px",
    lineHeight: "1.45",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "1.4",
    maxWidth: 300,
    margin: "0 auto",
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "none",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #05495a",
  borderRadius: "16px",
  padding: "32px 28px 32px 32px",
  width: "480px",
  position: "relative",
  flexShrink: 0,
  minHeight: 500,
  [theme.breakpoints.down("lg")]: {
    width: "420px",
    padding: "28px 24px 28px 28px",
    minHeight: 540,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "480px",
    padding: "24px 18px 24px 24px",
    minHeight: 520,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 14px 20px 20px",
    borderRadius: "12px",
    margin: "0 6px",
    maxWidth: "90%",
    minHeight: 500,
  },
}));

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  width: "100%",
  height: "100%",
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "32px",
  alignItems: "flex-start",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    gap: "10px",
  },
}));

const BackButton = styled(Button)({
  minWidth: "32px",
  width: "32px",
  height: "32px",
  padding: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
});

const TitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "8px",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 0px",
  flex: 1,
  [theme.breakpoints.down("md")]: {
    padding: 0,
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  height: "35px",
  flex: 1,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
    lineHeight: "30px",
  },
}));

const PageSubtitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "20px",
  lineHeight: "1.5",
  color: "#262626",
  textAlign: "center",
  width: "100%",
});

const OTPContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "40px",
  alignItems: "center",
  padding: "24px 0",
  flex: 1,
  width: "100%",
});

const OTPInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "31px",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    gap: "12px",
  },
}));

const OTPInput = styled(TextField)(({ theme }) => ({
  width: "52px",
  height: "52px",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    width: "52px",
    height: "52px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: 0,
    fontSize: "18px",
    fontFamily: "Rethink Sans, sans-serif",
    textAlign: "center",
    fontWeight: 600,
  },
  [theme.breakpoints.down("sm")]: {
    width: "44px",
    height: "44px",
    "& .MuiOutlinedInput-root": {
      width: "44px",
      height: "44px",
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "16px",
    },
  },
}));

const ResendContainer = styled(Box)({
  textAlign: "center",
});

const ResendText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "18px",
  lineHeight: "normal",
  color: "#374151",
});

const ResendLink = styled("span")({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "18px",
  fontWeight: 700,
  color: "#0a829d",
  cursor: "pointer",
  textDecoration: "underline",
  "&:hover": {
    color: "#086e86",
  },
});

const ContinueButton = styled(Button)<{ isActive: boolean }>(
  ({ isActive }) => ({
    backgroundColor: isActive
      ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
      : "#ddf0f4",
    border: "1px solid #ffffff",
    borderRadius: "60px",
    height: "60px",
    width: "100%",
    padding: "12px 24px",
    textTransform: "none",
    fontFamily: "Rethink Sans, sans-serif",
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "22px",
    color: isActive ? "#ffffff" : "#bad6dc",
    background: isActive
      ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
      : "#ddf0f4",
    boxShadow: isActive
      ? "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)"
      : "none",
    "&:hover": {
      background: isActive
        ? "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)"
        : "#ddf0f4",
    },
    "&:disabled": {
      background: "#ddf0f4",
      color: "#bad6dc",
    },
  })
);

interface LocationState {
  email?: string;
  fromSignup?: boolean;
  /** Landing vertical from /aviation, /healthcare, … — scopes post-verify pricing redirect. */
  vertical?: string | null;
}

export default function EmailVerification() {
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const state =
    (location.state as LocationState | null)?.email
      ? (location.state as LocationState)
      : getPendingSignupVerification();
  const email = state?.email;
  const fromSignup = state?.fromSignup || false;

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    if (!email) {
      navigate(fromSignup ? "/signup" : "/login");
      return;
    }
  }, [email, navigate, fromSignup]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (fromSignup) {
      navigate("/signup");
    } else {
      navigate("/login");
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);
    const newOtp = ["", "", "", ""];

    for (let i = 0; i < pasteData.length; i++) {
      newOtp[i] = pasteData[i];
    }

    setOtp(newOtp);

    const lastIndex = Math.min(pasteData.length - 1, 3);
    inputRefs[lastIndex].current?.focus();
  };

  const handleResend = async () => {
    if (!email || resending) return;

    setResending(true);
    setError(null);
    setSuccess(null);

    try {
      await resendOTP(email);
      setSuccess("OTP sent successfully!");
      setOtp(["", "", "", ""]);
      inputRefs[0].current?.focus();
    } catch (err: any) {
      let errorMessage = "Failed to resend OTP. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || otp.some((digit) => !digit)) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const otpString = otp.join("");
      const response = await verifyOTP(email, otpString);

      if (response.success) {
        clearPendingSignupVerification();
        setSuccess("Email verified successfully!");
        
        // Send welcome email if this is from signup process
        if (fromSignup) {
          try {
            // Get user_id from response or localStorage
            const userData = response.data?.user;
            let userId: number | undefined;
            
            // Try to get user_id from response first
            if (userData) {
              const id = (userData as any)?.id || (userData as any)?.user_id;
              if (id !== undefined && id !== null) {
                userId = Number(id);
              }
            }
            
            // Fallback: get from localStorage if not in response
            if (!userId || isNaN(userId)) {
              const userStr = localStorage.getItem("user");
              if (userStr) {
                try {
                  const user = JSON.parse(userStr);
                  const id = user?.id || user?.user_id;
                  if (id !== undefined && id !== null) {
                    userId = Number(id);
                  }
                } catch {
                  // Ignore parse errors
                }
              }
            }
            
            // if (userId && !isNaN(userId) && email) {
            //   // Call welcome email API (don't block navigation on failure)
            //   sendWelcomeEmail(userId, email).catch((err) => {
            //     console.error("Failed to send welcome email:", err);
            //     // Don't show error to user - welcome email is non-critical
            //   });
            // }
          } catch (welcomeEmailError) {
            console.error("Error sending welcome email:", welcomeEmailError);
            // Continue with navigation even if welcome email fails
          }
        }
        
        // Token is stored by verifyOTP. Prefer payment-pending preferred facility
        // matching the landing vertical (e.g. aviation), never a cross-vertical one.
        const landingVertical = state?.vertical ?? null;
        if (landingVertical) {
          setLandingVerticalSession(landingVertical);
        }
        const paymentPendingFacility = await tryGetSinglePaymentPendingFacilityForPricing(
          landingVertical,
        );
        const facilityTypeId = paymentPendingFacility
          ? (paymentPendingFacility.facility_details as any)?.facility_type_id ?? null
          : null;
        setTimeout(() => {
          if (paymentPendingFacility) {
            navigate("/dashboard/facilities", {
              replace: true,
              state: {
                currentView: "pricing",
                fromPreferredFacilityId: paymentPendingFacility.id,
                facility_type_id: facilityTypeId,
                pricingReloadToken: Date.now(),
              },
            });
          } else {
            navigate("/dashboard/facilities", {
              replace: true,
              state: { currentView: "MyFacility", showMyFacilityDialog: true },
            });
          }
        }, 1500);
      } else {
        setError(response.message || "OTP verification failed");
      }
    } catch (err: any) {
      let errorMessage = "OTP verification failed. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isButtonActive = otp.every((digit) => digit !== "");

  if (!email) {
    return null;
  }

  return (
    <SignUpContainer>
      <ContactSection>
        <BackgroundOverlay />

        <LeftPanel>
          <LeftPanelContent>
            <MainTitle>Verify Your Email</MainTitle>
            <SubTitle>
              We've sent a verification code to your email address. Please enter
              the code to complete your registration.
            </SubTitle>
          </LeftPanelContent>
        </LeftPanel>

        <RightPanel>
          <FormContainer>
            <HeaderContainer>
              <BackButton onClick={handleBack}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                    fill="currentColor"
                  />
                </svg>
              </BackButton>
              <TitleContainer>
                <PageTitle>Verify Email</PageTitle>
              </TitleContainer>
            </HeaderContainer>

            <PageSubtitle>
              Enter the 4-digit code sent to {email}
            </PageSubtitle>

            <OTPContainer>
              <Box
                component="form"
                onSubmit={onSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "40px",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <OTPInputContainer>
                  {otp.map((digit, index) => (
                    <OTPInput
                      key={index}
                      inputRef={inputRefs[index]}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      inputProps={{
                        maxLength: 1,
                        autoComplete: "off",
                      }}
                      variant="outlined"
                    />
                  ))}
                </OTPInputContainer>

                <ResendContainer>
                  <ResendText>
                    Didn't receive the email?{" "}
                    <ResendLink
                      onClick={handleResend}
                      style={{ opacity: resending ? 0.6 : 1 }}
                    >
                      {resending ? "Resending..." : "Resend"}
                    </ResendLink>
                  </ResendText>
                </ResendContainer>
              </Box>
            </OTPContainer>

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{ mt: -2, textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            {success && (
              <Typography
                color="success.main"
                variant="body2"
                sx={{ mt: -2, textAlign: "center" }}
              >
                {success}
              </Typography>
            )}

            <ContinueButton
              type="submit"
              disabled={loading || !isButtonActive}
              isActive={isButtonActive}
              onClick={onSubmit}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CircularProgress size={20} color="inherit" />
                  VERIFYING...
                </Box>
              ) : (
                "VERIFY OTP"
              )}
            </ContinueButton>
          </FormContainer>
        </RightPanel>
      </ContactSection>
    </SignUpContainer>
  );
}

